/**
 * Cold-start helpers for splash gating — banners + hero media when logged out.
 */
import { Image } from 'react-native';
import type { QueryClient } from '@tanstack/react-query';
import {
  bannersQueryKey,
  fetchBanners,
  getBannerImageUrl,
  pickMainHeroBanner,
  resolveBannerHeroMedia,
  type Banner,
} from '../data/domains/config';

async function prefetchUri(uri: string | null | undefined): Promise<void> {
  if (typeof uri !== 'string' || uri.length === 0) {
    return;
  }
  try {
    await Image.prefetch(uri);
  } catch {
    /* still allow splash exit — media may stream later */
  }
}

/** Same filter as `useBanners('banner')` on classic main screen. */
function filterClassicBannerList(banners: Banner[]): Banner[] {
  return banners.filter(banner => {
    const t = `${banner?.title ?? ''} ${banner?.arabic_title ?? ''}`.toLowerCase();
    return t.includes('banner');
  });
}

/** Prefetch the main-screen hero still (and image fallback) so the first paint is ready. */
export async function preloadMainBannerMedia(banners: Banner[]): Promise<void> {
  const hero = pickMainHeroBanner(banners);
  if (!hero) {
    return;
  }
  const image_url = getBannerImageUrl(hero);
  await prefetchUri(image_url);
  const media = resolveBannerHeroMedia(hero);
  if (
    media?.fallback_kind === 'image' &&
    media.fallback_uri &&
    media.fallback_uri !== image_url
  ) {
    await prefetchUri(media.fallback_uri);
  }
}

/** Guest / logged-out: fetch banners and warm the hero asset. Authenticated: no-op. */
export async function ensureLaunchBannersReady(
  query_client: QueryClient,
  is_authenticated: boolean,
): Promise<void> {
  if (is_authenticated) {
    return;
  }
  try {
    const banners = await query_client.fetchQuery({
      queryKey: bannersQueryKey,
      queryFn: fetchBanners,
      staleTime: 1000 * 60 * 10,
    });
    const list = Array.isArray(banners) ? banners : [];
    const classic = filterClassicBannerList(list);
    await preloadMainBannerMedia(classic.length > 0 ? classic : list);
  } catch {
    /* splash may still dismiss — main screen handles empty banners */
  }
}
